import React, { useEffect, useState, useMemo } from "react";
import { apiConstants, get, getDownload, postMultipart } from "../../common/constants";
import { FormProvider, RHFFileField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { Box, Button, Card, CardContent, Link, Typography, Paper } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { formatDate } from "../../helpers/UtilityHelper";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { FileDownloadOutlined } from "@mui/icons-material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useSnackbar } from "notistack";
import MUIModal from "../../components/MuiModal";
import PageTitle from "../../components/PageTitle";
import Loader from "../../common/Loader";
import jwt_decode from "jwt-decode";
import { getToken } from '../../helpers/ReduxHelpers';
import { statusBadgeSx, kpiCardSx, kpiRowSx, filterButtonSx, accentColor } from '../../common/sharedStyles';
import palette from '../../../theme/palette';
import { DownloadWithPolicy } from '../../common/DownloadPolicyDialog';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold }) => (
    <Box sx={{ fontSize: 12.5, lineHeight: 1.4, color: muted ? 'text.secondary' : 'text.primary', fontWeight: bold ? 600 : 400 }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: palette.text.disabled, textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

function ProgressBadge({ status }) {
    const ok = status === 'COMPLETED';
    return (
        <Box component="span" sx={{
            ...statusBadgeSx(ok ? 'success' : 'warning'),
            textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '10px',
        }}>
            {status || '—'}
        </Box>
    );
}

function ViewButton({ onClick, label }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            sx={{
                display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px',
                borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                bgcolor: 'transparent', color: palette.primary.main, border: `1px solid ${palette.primary.lighter}`,
                transition: 'all 0.15s', '&:hover': { bgcolor: palette.info.lighter, borderColor: palette.info.light },
            }}
        >
            {label}
        </Box>
    );
}

// ─── Upload KPI Row ───────────────────────────────────────────────────────────

function UploadKPIRow({ uploads }) {
    const kpis = useMemo(() => {
        if (!uploads || !uploads.length) return null;
        const total = uploads.length;
        const completedCount = uploads.filter(u => u.fileProgress === 'COMPLETED').length;
        const successRate = uploads.reduce((sum, u) => {
            const t = u.executionStatus?.totalTasks || 0;
            const s = u.executionStatus?.success || 0;
            return t > 0 ? sum + (s / t) : sum;
        }, 0);
        const avgSuccessRate = completedCount > 0 ? ((successRate / completedCount) * 100).toFixed(1) : '—';
        const lastUpload = uploads[0];
        return { total, avgSuccessRate, lastStatus: lastUpload?.fileProgress || '—' };
    }, [uploads]);
    if (!kpis) return null;

    const cards = [
        { label: 'Total Uploads', value: kpis.total, color: 'primary' },
        { label: 'Avg Success Rate', value: `${kpis.avgSuccessRate}%`, color: 'success' },
        { label: 'Last Upload', value: kpis.lastStatus, color: kpis.lastStatus === 'COMPLETED' ? 'success' : 'warning' },
    ];

    return (
        <Box sx={kpiRowSx}>
            {cards.map(c => (
                <Paper key={c.label} sx={kpiCardSx(c.color)}>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem' }}>
                        {c.label}
                    </Typography>
                    <Typography variant='h5' sx={{ mt: 0.5, fontWeight: 800, color: accentColor(c.color) }}>
                        {c.value}
                    </Typography>
                </Paper>
            ))}
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

const NDRBulkUpload = () => {
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [errorMsg, setErrorMsg] = useState();
    const [dataa, setData] = useState([]);
    const [page, setPage] = useState(0);
    const methods = useForm();
    const [sizePerPage, setSizePerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(0);
    const [page1, setPage1] = useState(0);
    const [sizePerPage1, setSizePerPage1] = useState(10);
    const [reload, setReload] = useState(0);
    const [seeOrders, setSeeOrders] = useState(false);
    const [orderData, setOrderData] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const { piiAccessAllow } = jwt_decode(getToken());
    const { handleSubmit: handleSubmitBulkUpload, reset: resetForm } = methods;

    const onSubmitBulkUpload = async (data) => {
        setIsUploadingFile(true);
        try {
            let form = new FormData();
            form.append("file", data.ndrFile[0]);
            form.append("remark", 'NDR Bulk Upload');
            await postMultipart(apiConstants.NDR_BULK_UPLOAD, form).then((res) => {
                setIsUploadingFile(false);
                if (res.status === 200) {
                    if (res.data && res.data?.status === 'ERROR') {
                        enqueueSnackbar("File Upload Failed", { variant: "error" });
                        setErrorMsg(res.data?.errorMsg);
                        setIsUploadingFile(false);
                    } else {
                        enqueueSnackbar("Successfully Uploaded!", { variant: "success" });
                        setIsUploadingFile(false);
                    }
                }
            });
            setReload(reload + 1);
            setIsUploadingFile(false);
            resetForm();
        } catch (err) {
            const message = err?.response?.data?.message || err.message;
            enqueueSnackbar(message || "Error uploading file", { variant: "error" });
            setIsUploadingFile(false);
        }
        resetForm({ ndrFile: '' });
        setIsUploadingFile(false);
    };

    const handleBulkSample = () => {
        getDownload(`${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=NDR_BULK_ACTION_V2`)
            .then((response) => {
                const binaryData = [];
                binaryData.push(response.data);
                const href = URL.createObjectURL(new Blob(binaryData, { type: "application/octet-stream" }));
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", "bulk-upload-sample.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch((err) => { console.error(err); });
    };

    useEffect(() => {
        setIsLoading(true);
        get(`${apiConstants.GET_BULK_UPLOAD_NDR}`).then((res) => {
            setData(res?.data?.responseObj);
        }).finally(() => setIsLoading(false));
    }, [reload]);

    const columnsOrderModal = [
        { field: "awb", headerAlign: "left", hideSortIcons: true, headerName: "AWB", flex: 0.7, renderCell: ({ row }) => <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{row?.data?.awbNumber}<CopyIcon text={row?.data?.awbNumber} /></Box> },
        { field: "action", headerAlign: "left", hideSortIcons: true, headerName: "NDR Action", flex: 0.7, renderCell: ({ row }) => row?.data?.action },
        { field: "remark", headerAlign: "left", hideSortIcons: true, headerName: "Remark", flex: 2, renderCell: ({ row }) => row?.response?.remark },
        { field: "actionBy", headerAlign: "left", hideSortIcons: true, headerName: "Action User", flex: 1, renderCell: ({ row }) => row?.data?.actionedBy },
    ];

    const columns = [
        {
            field: 'id',
            headerName: 'ID', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 200,
            renderCell: ({ row }) => <CellLine muted>{row.id}</CellLine>,
        },
        ...(piiAccessAllow ? [{
            field: 'file',
            headerName: 'File', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150,
            renderCell: ({ row }) => (
                <Box
                    component={Link}
                    href={row?.s3RefToFile?.signedLink}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ fontSize: '12px', color: palette.primary.main, textDecoration: 'none', borderBottom: `1px dashed ${palette.info.light}`, '&:hover': { borderBottomStyle: 'solid' } }}
                >
                    View
                </Box>
            ),
        }] : []),
        {
            field: 'fileProgress',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150,
            renderCell: ({ row }) => <ProgressBadge status={row?.fileProgress} />,
        },
        {
            field: 'createdAt',
            headerName: 'Upload Date', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180,
            renderCell: ({ row }) => <CellLine muted>{formatDate(row?.createdAt)}</CellLine>,
        },
        {
            field: 'totalRecords',
            headerName: 'Upload Stats', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 200,
            renderCell: ({ row }) => (
                <CellStack>
                    <Box sx={{ fontSize: '12px', color: 'text.primary' }}><DataLabel>Total</DataLabel>{row?.executionStatus?.totalTasks ?? 0}</Box>
                    <Box sx={{ fontSize: '12px', color: palette.success.main }}><DataLabel>Success</DataLabel>{row?.executionStatus?.success ?? 0}</Box>
                    <Box sx={{ fontSize: '12px', color: palette.error.main }}><DataLabel>Failed</DataLabel>{row?.executionStatus?.failed ?? 0}</Box>
                </CellStack>
            ),
        },
        {
            field: 'successOrders',
            headerName: 'Successful', headerAlign: 'center', align: 'center',
            hideSortIcons: true, minWidth: 200,
            renderCell: ({ row }) => {
                if (!row?.executionStatus?.success) return <CellLine muted>—</CellLine>;
                return (
                    <ViewButton
                        label={`View (${row.executionStatus.success})`}
                        onClick={() => {
                            const flat = [].concat(...row?.taskListBatch);
                            setOrderData(flat.filter((item) => item?.status === "SUCCESS"));
                            setSeeOrders(true);
                        }}
                    />
                );
            },
        },
        {
            field: 'failedOrders',
            headerName: 'Failed', headerAlign: 'center', align: 'center',
            hideSortIcons: true, minWidth: 200,
            renderCell: ({ row }) => {
                if (!row?.executionStatus?.failed) return <CellLine muted>—</CellLine>;
                return (
                    <ViewButton
                        label={`View (${row.executionStatus.failed})`}
                        onClick={() => {
                            const flat = [].concat(...row?.taskListBatch);
                            setOrderData(flat.filter((item) => item?.status !== "SUCCESS"));
                            setSeeOrders(true);
                        }}
                    />
                );
            },
        },
    ];

    return (
        <>
            {isLoading && <Loader />}
            <PageTitle>NDR Bulk Actions</PageTitle>

            {/* ── Upload KPI Cards ─────────────────────────────────────── */}
            <UploadKPIRow uploads={dataa} />

            {/* ── Upload form ───────────────────────────────────────────── */}
            <Card elevation={0} sx={{ mb: 2, borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Upload NDR Bulk Actions</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>CSV • Maximum 500 NDR actions per file</Typography>
                </Box>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmitBulkUpload(onSubmitBulkUpload)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <RHFFileField
                                required={true}
                                fileType={['csv']}
                                name="ndrFile"
                                label="Click to upload CSV file"
                            />
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <LoadingButton
                                    startIcon={<FileUploadIcon />}
                                    type="submit"
                                    loading={isUploadingFile}
                                    variant="contained"
                                    size="small"
                                    disableElevation
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, ...filterButtonSx('apply') }}
                                >
                                    Upload
                                </LoadingButton>
                                <DownloadWithPolicy onConfirm={handleBulkSample} reportName="NDR Bulk Sample">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<FileDownloadOutlined />}
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 0.85, color: palette.primary.main, borderColor: palette.primary.main, '&:hover': { bgcolor: palette.primary.lighter } }}
                                    >
                                        Sample
                                    </Button>
                                </DownloadWithPolicy>
                            </Box>
                            {errorMsg && (
                                <Box sx={{ fontSize: '12px', color: palette.error.dark, bgcolor: palette.error.lighter, border: `1px solid ${palette.error.light}`, borderRadius: '6px', px: 2, py: 1.5 }}>
                                    0 records succeeded. Error: {errorMsg}. Please fix the file and re-upload.
                                </Box>
                            )}
                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Upload history ────────────────────────────────────────── */}
            <Box sx={{ bgcolor: palette.background.paper, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    rows={dataa?.length > 0 ? dataa : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    paginationMode={'client'}
                    getRowHeight={() => 'auto'}
                    rowCount={dataa?.length}
                    getRowId={(row) => row.id}
                />
            </Box>

            <MUIModal open={seeOrders} title="AWB Details" maxWidth={"lg"} handleClose={() => setSeeOrders(false)}>
                <ProzoDataGrid
                    sizePerPage={sizePerPage1}
                    setSizePerPage={setSizePerPage1}
                    page={page1}
                    setPage={setPage1}
                    rowCount={orderData?.length}
                    columns={columnsOrderModal}
                    rows={orderData}
                    paginationMode={'client'}
                    pagination={true}
                    hideFooterPagination={false}
                    getRowId={(row) => Math.random()}
                />
            </MUIModal>
        </>
    );
};

export default NDRBulkUpload;
